import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFloppyDisk, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useControlCenterOperationConfig } from '../../hooks/useControlCenterOperationConfig'
import type {
  ConfigurableOperatorId,
  ControlCenterOperationConfig,
} from '../../utils/controlCenterOperationConfig'
import { OPERATORS } from './operations'

/**
 * Control Center Management → Operation Config.
 * Operator enable flags only. Overtime and pay-period settings live in HR → Payroll Tools.
 */
export default function OperationConfigManager() {
  const { config, loading, error, save, refresh } = useControlCenterOperationConfig()
  const [draft, setDraft] = useState<ControlCenterOperationConfig>(config)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  useEffect(() => {
    setDraft(config)
  }, [config])

  const setOperatorEnabled = (id: ConfigurableOperatorId, enabled: boolean) => {
    setDraft((prev) => ({
      ...prev,
      enabledOperators: {
        ...prev.enabledOperators,
        [id]: enabled,
      },
    }))
    setSaveMessage(null)
  }

  const onSave = async () => {
    setSaving(true)
    setSaveMessage(null)
    try {
      await save({ enabledOperators: draft.enabledOperators })
      setSaveMessage('Saved.')
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading && !config) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-base-content/70">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
        Loading operation config…
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl space-y-6 p-4">
        {error ? (
          <div className="alert alert-warning text-sm">
            <span>{error}</span>
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => void refresh()}>
              Retry
            </button>
          </div>
        ) : null}

        <section className="rounded-lg border border-base-300 bg-base-100 p-4">
          <h3 className="text-sm font-semibold">Available operations</h3>
          <p className="mt-0.5 text-xs text-base-content/55">
            Enable or disable operations shown on the map and in the output panel. All operations
            are enabled by default. Overtime and pay-period settings are in HR → Payroll Tools.
          </p>
          <ul className="mt-3 divide-y divide-base-200">
            {OPERATORS.map((op) => {
              const enabled = draft.enabledOperators?.[op.id as ConfigurableOperatorId] !== false
              return (
                <li key={op.id} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                  <input
                    id={`op-enabled-${op.id}`}
                    type="checkbox"
                    className="checkbox checkbox-sm mt-0.5"
                    checked={enabled}
                    onChange={(e) =>
                      setOperatorEnabled(op.id as ConfigurableOperatorId, e.target.checked)
                    }
                  />
                  <label htmlFor={`op-enabled-${op.id}`} className="min-w-0 cursor-pointer">
                    <div className="text-sm font-medium leading-tight">{op.label}</div>
                    <div className="mt-0.5 text-xs leading-snug text-base-content/55">
                      {op.description}
                    </div>
                  </label>
                </li>
              )
            })}
          </ul>
        </section>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm gap-2"
            disabled={saving}
            onClick={() => void onSave()}
          >
            <FontAwesomeIcon
              icon={saving ? faSpinner : faFloppyDisk}
              className={saving ? 'animate-spin' : ''}
            />
            Save
          </button>
          {saveMessage ? (
            <span className="text-sm text-base-content/60">{saveMessage}</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}
